import {
  AccountInfo,
  Configuration,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser'
import { BaseAuthService } from '@/core/modules/auth/BaseAuthService'

/**
 * MSAL authentication service.
 */
export class MsalAuthService extends BaseAuthService<AccountInfo, PublicClientApplication> {
  private scopes = [ 'openid', import.meta.env.VITE_AZURE_AD_SCOPE_API || '' ]

  /**
   * @inheritDoc
   */
  public user = () => this.authClient.getActiveAccount()

  private static config: Configuration = {
    auth: {
      clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || '',
      authority: import.meta.env.VITE_AZURE_AD_AUTHORITY,
      redirectUri: import.meta.env.VITE_AZURE_AD_REDIRECT_URI || '',
      postLogoutRedirectUri: import.meta.env.VITE_AZURE_AD_POST_LOGOUT_REDIRECT_URI || '',
    },
    cache: {
      cacheLocation: 'localStorage',
      /*
       * Purge the pre-v5 (msal-browser v4) cache format on first init after the upgrade
       * instead of retaining it for the default 5 days. Stale v4 entries make
       * handleRedirectPromise throw no_token_request_cache_error. See v5 migration guide.
       */
      cacheRetentionDays: 0,
    },
  }

  // Loop guard for the interactive re-auth redirect in getAccessToken (per-tab, time-boxed).
  private static readonly reauthRedirectKey = 'msal:reauth-redirect-at'

  private static readonly reauthRedirectCooldownMs = 120_000

  /*
   * Concurrent silent token requests are fine (MSAL dedupes them), but concurrent *interactive*
   * calls are not: a second acquireTokenRedirect() while one is already in flight throws
   * BrowserAuthError "interaction_in_progress" instead of InteractionRequiredAuthError, which
   * getAccessToken rethrows as an uncaught error. This is common right when the refresh token
   * expires (~24h), since several in-flight API calls hit the silent-auth failure at once.
   * Memoizing the redirect promise ensures only the first caller triggers acquireTokenRedirect;
   * the rest just await the same in-flight redirect instead of racing to start their own.
   */
  private reauthRedirectPromise: Promise<void> | null = null

  constructor() {
    super(new PublicClientApplication(MsalAuthService.config))
  }

  /**
   * @inheritDoc
   */
  async init() {
    await this.authClient.initialize()

    try {
      await this.authClient.handleRedirectPromise()
    } catch (error) {
      /*
       * A stuck `interaction_in_progress` flag with no matching cached request makes
       * handleRedirectPromise throw (no_token_request_cache_error) on every load. This
       * happens when a prior interactive call (e.g. an acquireTokenPopup fallback) was
       * blocked/interrupted and never cleared its state. clearCache() removes the stray
       * MSAL keys so the app recovers to a clean unauthenticated state instead of looping.
       */
      console.error('handleRedirectPromise failed, clearing stale MSAL state', error)
      await this.authClient.clearCache().catch(() => {})
    }

    const accounts = this.authClient.getAllAccounts()

    if (accounts.length > 0) {
      await this.setAccount(accounts[0])
    }
  }

  /**
   * @inheritDoc
   */
  async isAuthenticated() {
    return !!this.user()
  }

  /**
   * @inheritDoc
   */
  async getAccessToken() {
    const user = this.user()

    if (!user) {
      return null
    }

    const request = {
      scopes: this.scopes,
      authority: import.meta.env.VITE_AZURE_AD_AUTHORITY,
      account: user,
      redirectUri: `${window.location.origin}/auth/redirect`,
    }

    try {
      const result = await this.authClient.acquireTokenSilent(request)

      // Silent acquisition works again — clear any pending re-auth loop guard.
      sessionStorage.removeItem(MsalAuthService.reauthRedirectKey)
      this.reauthRedirectPromise = null

      return result.accessToken
    } catch (error) {
      if (!(error instanceof InteractionRequiredAuthError)) {
        throw error
      }

      /*
       * Another concurrent call already kicked off the interactive redirect — piggyback on it
       * instead of calling acquireTokenRedirect again (which would throw interaction_in_progress).
       */
      if (this.reauthRedirectPromise) {
        await this.reauthRedirectPromise

        return null
      }

      /*
       * The refresh token is gone/expired, so a genuine interactive sign-in is required. We use a
       * top-level redirect (a popup would be blocked without a user gesture and leave MSAL's
       * interaction state stuck). This only happens once the refresh token has fully expired
       * (~24h), not on ordinary access-token renewal (acquireTokenSilent handles that via the
       * refresh token, no interaction). The sessionStorage marker prevents an infinite redirect
       * loop if we return from the redirect and silent acquisition still fails.
       */
      const lastRedirectAt = Number(sessionStorage.getItem(MsalAuthService.reauthRedirectKey)) || 0
      if (lastRedirectAt && Date.now() - lastRedirectAt < MsalAuthService.reauthRedirectCooldownMs) {
        sessionStorage.removeItem(MsalAuthService.reauthRedirectKey)
        console.error('Silent token still failing right after interactive re-auth; not redirecting again')

        return null
      }

      sessionStorage.setItem(MsalAuthService.reauthRedirectKey, String(Date.now()))
      this.reauthRedirectPromise = this.authClient.acquireTokenRedirect(request).catch(() => {})
      await this.reauthRedirectPromise

      return null
    }
  }

  /**
   * @inheritDoc
   */
  async logout() {
    if (!this.user()) {
      return false
    }
    /*
     * clear local storage instead of logoutRedirect, otherwise user will sign out from all apps
     * await this.authClient.logoutRedirect()
     */

    localStorage.clear()

    return true
  }

  private async setAccount(account: AccountInfo | null) {
    this.authClient.setActiveAccount(account)

    return account
  }

  async login(): Promise<void> {
    /*
     * Use a top-level redirect rather than a popup. Once async work has run the click is no
     * longer a trusted user gesture, so Firefox/Safari block the popup, and a blocked or closed
     * popup leaves MSAL's `interaction_in_progress` flag stuck — which then makes
     * handleRedirectPromise throw no_token_request_cache_error on every subsequent load. A
     * redirect can't be blocked and completes sign-in in every browser. This navigates away; the
     * response is processed by handleRedirectPromise() in init() on return, after which
     * navigateToLoginRequestUrl sends the browser back to the page that started the login.
     */
    await this.authClient.loginRedirect({
      scopes: this.scopes,
      redirectUri: `${window.location.origin}/auth/redirect`,
    })
  }
}
